import { useQuery } from '@tanstack/react-query';
import { PieChart as PieIcon } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { apiClient } from '@/lib/api-client';
import { CardShell, EmptyState } from '../cards/card-shell';
import { CHART_COLORS, formatAgeRange } from './chart-colors';

export function AgeRangesWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['clients', 'stats', 'charts'],
    queryFn: () => apiClient.clientsStatsCharts(),
    staleTime: 1000 * 60,
  });

  const rows = (data?.ageRanges ?? [])
    .filter((r) => r.count > 0)
    .map((r) => ({
      name: formatAgeRange(r.range),
      value: r.count,
    }));

  return (
    <CardShell title="Faixa etária" icon={PieIcon}>
      <div className="flex h-full w-full items-center gap-3">
        {isLoading ? (
          <div className="h-full w-full animate-pulse rounded bg-[var(--surface-muted)]/40" />
        ) : isError ? (
          <p className="text-xs text-[var(--fg-muted)]">
            Não foi possível carregar.
          </p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={PieIcon}
            message="Nenhum cliente com data de nascimento."
          />
        ) : (
          <>
            <div className="h-full flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rows}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="55%"
                    outerRadius="85%"
                    paddingAngle={2}
                    stroke="var(--card-bg)"
                  >
                    {rows.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={CHART_COLORS[idx % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'var(--fg)',
                    }}
                    labelStyle={{ color: 'var(--fg)' }}
                    itemStyle={{ color: 'var(--fg)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="flex flex-1 flex-col gap-1.5 text-xs">
              {rows.map((r, idx) => (
                <li
                  key={r.name}
                  className="flex items-center gap-2 text-[var(--fg-muted)]"
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{
                      background: CHART_COLORS[idx % CHART_COLORS.length],
                    }}
                  />
                  <span className="truncate">{r.name}</span>
                  <span className="ml-auto font-medium text-[var(--fg)]">
                    {r.value}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </CardShell>
  );
}
